import {
  type ChartExperimentEventMatchConfig,
  type EventDataItem,
  type EventDataMap,
  type EventTypeCollapseConfig,
  type ExperimentTypeFixedLabelParams,
  type IDownloadColumnForEventMatch,
  type IFormattedEventRecord,
  type IRawEventDayItem,
  type TKpiEventTypeBarColorMap,
  type TKpiEventTypeColorEntry,
  type TKpiEventTooltipColorEntry,
  type TKpiXAxisBubbleLabelColors,
  type TRawEventRecord,
  type TEventTypeBarColorMapConfig,
  type TEventTypeBarColorValue,
  type RawEventItem,
  type TEventStartTimeAxisClipMode,
  EVENT_TYPE_BAR_COLOR_MAP_KEY_KPI_X_AXIS_BUBBLE,
} from './types';
import moment from 'moment';

const DEFAULT_EXPERIMENT_FIXED_CN = 'A/B 实验';
const DEFAULT_EXPERIMENT_FIXED_EN = 'A/B Test';

const formatCountSuffix = (template: string, count: number): string => {
  if (typeof template !== 'string' || !template) {
    return '';
  }
  return template.replace(/\{count\}/g, String(count));
};

/** 归一化「可合并类型」匹配词列表 */
const buildEventCollapsePatternList = (
  config: EventTypeCollapseConfig,
): string[] => {
  const primary = String(config.matchEventType ?? '').trim();
  const fromExtra = (config.matchEventTypes ?? [])
    .map(s => String(s).trim())
    .filter(Boolean);
  const merged = [...(primary ? [primary] : []), ...fromExtra];
  return [...new Set(merged)];
};

/**
 * KPI 事件卡：`event_type` / `eventType` 经 `resolveEventTypeDisplayOrderKey` 后约定为「实验」。
 * 非 KPI 或类型名不一致时勿依赖此常量，应写 `eventTypeCollapse` 或 `chartEventExperimentTooltip`。
 */
export const KPI_DEFAULT_EXPERIMENT_EVENT_TYPE_MATCH = '实验';

export type TResolveEventTypeCollapseOptions = {
  /**
   * 无显式 `eventTypeCollapse`、且未 `chartEventExperimentTooltip.enabled === false`、
   * 且未自定义 `matchNameCn` / `matchNameEn` 时，按 `KPI_DEFAULT_EXPERIMENT_EVENT_TYPE_MATCH` 生成合并规则。
   */
  useKpiExperimentMatchDefaults?: boolean;
};

/**
 * 解析事件合并配置：优先 `chartConfig.eventTypeCollapse`；
 * 否则从 `chartEventExperimentTooltip.matchNameCn/En` 推导；KPI 默认见 `KPI_DEFAULT_EXPERIMENT_EVENT_TYPE_MATCH`。
 */
export function resolveEventTypeCollapseConfig(
  chartConfig:
  | {
    eventTypeCollapse?: EventTypeCollapseConfig;
    chartEventExperimentTooltip?: ChartExperimentEventMatchConfig;
  }
  | undefined,
  options?: TResolveEventTypeCollapseOptions,
): EventTypeCollapseConfig | undefined {
  const explicit = chartConfig?.eventTypeCollapse;
  const patternsFromExplicit = explicit
    ? buildEventCollapsePatternList(explicit)
    : [];
  if (patternsFromExplicit.length > 0) {
    return {
      ...explicit,
      matchEventType:
        String(explicit.matchEventType ?? '').trim() || patternsFromExplicit[0],
      matchEventTypes:
        patternsFromExplicit.length > 1 ? patternsFromExplicit : undefined,
      totalSuffixCn: explicit.totalSuffixCn,
      totalSuffixEn: explicit.totalSuffixEn,
    };
  }

  const rawTip = chartConfig?.chartEventExperimentTooltip;
  const useKpiDefault = Boolean(options?.useKpiExperimentMatchDefaults);

  if (rawTip?.enabled === false) {
    return undefined;
  }

  const hasCustomTipPatterns =
    Boolean(rawTip?.matchNameCn?.length) ||
    Boolean(rawTip?.matchNameEn?.length);

  if (useKpiDefault && !hasCustomTipPatterns) {
    return {
      matchEventType: KPI_DEFAULT_EXPERIMENT_EVENT_TYPE_MATCH,
      totalSuffixCn: '（{count}）',
      totalSuffixEn: ' ({count})',
    };
  }

  const labels = [
    ...(rawTip?.matchNameCn ?? []),
    ...(rawTip?.matchNameEn ?? []),
  ]
    .map(s => String(s).trim())
    .filter(Boolean);
  const uniq = [...new Set(labels)];
  if (!uniq.length) {
    return undefined;
  }
  return {
    matchEventType: uniq[0],
    matchEventTypes: uniq.length > 1 ? uniq : undefined,
    totalSuffixCn: '（{count}）',
    totalSuffixEn: ' ({count})',
  };
}

/**
 * 同一日期下，将 matchEventType 对应的多条事件合并为一条（取首条文案 + 总条数后缀），其它 event_type 不变。
 * 未配置、或该日无多条匹配时原样返回。
 * experimentFixed：开启时实验类多条合并为 **单条** 展示：主文案为 `A/B 实验(n)` / `A/B Test(n)`（半角括号条数），
 * 不再拼接 `totalSuffix` 模板。`description` 等置空：条上已有 name，避免与舆情 EventItem 内 CommonBubble 的 content 重复触发 hover 气泡；亦不保留首条原文以免误导。
 */
export const applyEventTypeCollapseToEventDataMap = (
  eventData: EventDataMap | undefined,
  config: EventTypeCollapseConfig | undefined,
  isEn: boolean,
  experimentFixed?: ExperimentTypeFixedLabelParams,
): EventDataMap | undefined => {
  const patternList = config ? buildEventCollapsePatternList(config) : [];
  if (!eventData || !config || patternList.length === 0) {
    return eventData;
  }

  const useFixed = Boolean(experimentFixed?.enabled);
  const labelCn =
    experimentFixed?.labelCn?.trim() || DEFAULT_EXPERIMENT_FIXED_CN;
  const labelEn =
    experimentFixed?.labelEn?.trim() || DEFAULT_EXPERIMENT_FIXED_EN;

  const out: EventDataMap = {};
  Object.keys(eventData).forEach(dateKey => {
    const list = eventData[dateKey];
    if (!Array.isArray(list) || list.length === 0) {
      out[dateKey] = list || [];
      return;
    }

    // 合并前后保持 EventDataItem 结构；用具体类型替代 any，记录 matched/others 分桶
    const matched: EventDataItem[] = [];
    const others: EventDataItem[] = [];
    list.forEach((item: EventDataItem) => {
      const t = item?.event_type ?? item?.eventType ?? '';
      if (patternList.some(p => matchesConfiguredEventTypeLabel(t, p))) {
        matched.push(item);
      } else {
        others.push(item);
      }
    });

    if (matched.length <= 1) {
      if (useFixed && matched.length === 1) {
        out[dateKey] = list.map((item: EventDataItem) => {
          const t = item?.event_type ?? item?.eventType ?? '';
          if (!patternList.some(p => matchesConfiguredEventTypeLabel(t, p))) {
            return item;
          }
          return {
            ...item,
            name: labelEn,
            name_cn: labelCn,
          };
        });
        return;
      }
      out[dateKey] = list;
      return;
    }

    const suffix = formatCountSuffix(
      isEn ? config.totalSuffixEn : config.totalSuffixCn,
      matched.length,
    );
    const first = matched[0];
    const nameBase = useFixed
      ? labelEn
      : first?.name ?? first?.event_title_en ?? '';
    const nameCnBase = useFixed
      ? labelCn
      : first?.name_cn ?? first?.event_title_cn ?? '';

    const count = matched.length;
    /** 固定 A/B 文案 + 半角括号条数：不再拼接 totalSuffix 模板，避免「名称 + 全角后缀」叠字 */
    const displayEnFixed = `${labelEn}(${count})`;
    const displayCnFixed = `${labelCn}(${count})`;

    const collapsed = useFixed
      ? {
        ...first,
        name: displayEnFixed,
        name_cn: displayCnFixed,
        description: '',
        description_cn: '',
        event_desc_en: '',
        event_desc_cn: '',
      }
      : {
        ...first,
        name: `${nameBase}${suffix}`,
        name_cn: `${nameCnBase}${suffix}`,
      };
    out[dateKey] = [...others, collapsed];
  });

  return out;
};

/**
 * 与 `chartConfig.eventTypeDisplayOrder` 对齐用的类型键；接口常为 `{ name_cn, name_en }`，不可 `String(对象)`。
 */
export function resolveEventTypeDisplayOrderKey(typeRaw: unknown): string {
  if (typeRaw === undefined || typeRaw === null) {
    return '';
  }
  if (typeof typeRaw === 'string') {
    return typeRaw.trim();
  }
  if (typeof typeRaw === 'object') {
    const o = typeRaw as {
      name_cn?: unknown;
      name_en?: unknown;
      name?: unknown;
    };
    const cn = String(o.name_cn ?? '').trim();
    if (cn) {
      return cn;
    }
    return String(o.name_en ?? o.name ?? '').trim();
  }
  return String(typeRaw).trim();
}

/**
 * 与配置项（如合并类型 `matchEventType`、配色 key）匹配：全等、忽略大小写、包含关系（label 长度≥2）。
 */
export function matchesConfiguredEventTypeLabel(
  typeRaw: unknown,
  label: string,
): boolean {
  const key = resolveEventTypeDisplayOrderKey(typeRaw);
  const m = String(label ?? '').trim();
  if (!key || !m) {
    return false;
  }
  if (key === m || key.toLowerCase() === m.toLowerCase()) {
    return true;
  }
  if (m.length < 2) {
    return false;
  }
  return key.includes(m) || m.includes(key);
}

function lookupEventTypeBarColorRaw(
  source: Record<string, TEventTypeBarColorValue>,
  typeRaw: unknown,
): TEventTypeBarColorValue | undefined {
  const key = resolveEventTypeDisplayOrderKey(typeRaw);
  if (!key) {
    return undefined;
  }
  const direct = source[key];
  if (direct !== undefined) {
    return direct;
  }
  const keys = Object.keys(source).filter(k => !k.startsWith('__'));
  const ci = keys.find(k => k.toLowerCase() === key.toLowerCase());
  if (ci !== undefined) {
    return source[ci];
  }
  const fuzzy = keys.find(
    token =>
      token.length >= 2 &&
      (key.includes(token) || token.includes(key)),
  );
  return fuzzy !== undefined ? source[fuzzy] : undefined;
}

/**
 * 同一日期内按 event_type 优先级排序（自上而下：数组靠前先展示）。
 * 未在列表中的类型排在已声明类型之后，同档保持原相对顺序。
 */
export const applyEventTypeDisplayOrderToEventDataMap = (
  eventData: EventDataMap | undefined,
  order: string[] | undefined,
): EventDataMap | undefined => {
  if (!eventData || !Array.isArray(order) || order.length === 0) {
    return eventData;
  }

  const normalizedOrder = order.map(o => String(o).trim());
  const rank = (typeRaw: unknown): number => {
    const key = resolveEventTypeDisplayOrderKey(typeRaw);
    if (!key) {
      return normalizedOrder.length;
    }
    let i = normalizedOrder.findIndex(
      x => x === key || x.toLowerCase() === key.toLowerCase(),
    );
    if (i !== -1) {
      return i;
    }
    /** 展示键与 `eventTypeDisplayOrder` 配置项（如「实验」）的包含关系（token 长度≥2） */
    i = normalizedOrder.findIndex(token => {
      if (!token || token.length < 2) {
        return false;
      }
      return key.includes(token) || token.includes(key);
    });
    return i !== -1 ? i : normalizedOrder.length;
  };

  const out: EventDataMap = {};
  Object.keys(eventData).forEach(dateKey => {
    const list = eventData[dateKey];
    if (!Array.isArray(list) || list.length <= 1) {
      out[dateKey] = Array.isArray(list) ? [...list] : [];
      return;
    }
    const indexed = list.map((item, idx) => ({ item, idx }));
    indexed.sort((a, b) => {
      const ra = rank(a.item?.event_type ?? a.item?.eventType);
      const rb = rank(b.item?.event_type ?? b.item?.eventType);
      if (ra !== rb) {
        return ra - rb;
      }
      return a.idx - b.idx;
    });
    out[dateKey] = indexed.map(x => x.item);
  });
  return out;
};

/** 顶层仅含 `xAxisBubble` / `eventTooltipsMap`，无「版更」等类型 key */
export function isTopLevelKpiEventBarColorSplit(
  m: TEventTypeBarColorMapConfig | undefined,
): m is TKpiEventTypeBarColorMap {
  if (!m || typeof m !== 'object' || Array.isArray(m)) {
    return false;
  }
  const keys = Object.keys(m);
  if (keys.length === 0) {
    return false;
  }
  return keys.every(k =>
    ['xAxisBubble', 'eventTooltipsMap'].includes(k),
  );
}

/** 按类型分组：`版更: { eventTooltips?, xAxisBubble? }`，对象项至少含其一（与扁平 `{ background, fontColor }` 区分） */
export function isPerTypeKpiEventBarColorMap(
  m: TEventTypeBarColorMapConfig | undefined,
): boolean {
  if (!m || typeof m !== 'object' || Array.isArray(m)) {
    return false;
  }
  if (isTopLevelKpiEventBarColorSplit(m)) {
    return false;
  }
  const flat = m as Record<string, unknown>;
  return Object.keys(flat).some(k => {
    if (k.startsWith('__')) {
      return false;
    }
    const v = flat[k];
    if (!v || typeof v !== 'object' || Array.isArray(v)) {
      return false;
    }
    return 'eventTooltips' in v || 'xAxisBubble' in v;
  });
}

/** 解析 eventTypeBarColorMap 单条（字符串或对象） */
export function parseEventTypeBarColorEntry(
  v: TEventTypeBarColorValue | undefined,
): { barBackground?: string; barFontColor?: string; } {
  if (v === undefined || v === null) {
    return {};
  }
  if (typeof v === 'string') {
    const s = v.trim();
    return s ? { barBackground: s } : {};
  }
  const bg =
    typeof v.background === 'string' && v.background.trim() !== ''
      ? v.background.trim()
      : undefined;
  const fg =
    typeof v.fontColor === 'string' && v.fontColor.trim() !== ''
      ? v.fontColor.trim()
      : undefined;
  return {
    ...(bg ? { barBackground: bg } : {}),
    ...(fg ? { barFontColor: fg } : {}),
  };
}

/**
 * 浮窗 EventTypeTag：`eventTooltips` 为底色默认；同类型若配置了 `xAxisBubble`，其 background/fontColor **覆盖** Tag 底/字色（舆情条仍只用 eventTooltips，见 applyEventTypeBarColorMapToEventDataMap）。
 */
export function mergeKpiTypeTagColors(
  entry: TKpiEventTypeColorEntry,
): { barBackground?: string; barFontColor?: string; } {
  const tip = parseEventTypeBarColorEntry(entry.eventTooltips);
  const xb = entry.xAxisBubble;
  let { barBackground, barFontColor } = tip;
  if (xb) {
    if (typeof xb.background === 'string' && xb.background.trim() !== '') {
      barBackground = xb.background.trim();
    }
    if (typeof xb.fontColor === 'string' && xb.fontColor.trim() !== '') {
      barFontColor = xb.fontColor.trim();
    }
  }
  return { barBackground, barFontColor };
}

/**
 * 供 KPI EventDetailsPop：event_type key → Tag 背景/字色（按类型合并 xAxisBubble + eventTooltips）。
 * @param tagStyleAliases 可选：chartConfig 级别别名表；与分组条目 / eventTooltipsMap 条目上的 `tagStyleAliases` 合并。
 * 写入 map 时对每个键同时注册 **原样** 与 **toLowerCase()**，EventDetailsPop 解析时对 name_en/name_cn 亦做忽略大小写回退。
 */
export function buildEventTypeTagStyleMapFromBarColorMap(
  colorMap: TEventTypeBarColorMapConfig | undefined,
  tagStyleAliases?: Record<string, string[]>,
): Record<string, { background: string; fontColor?: string; }> {
  const out: Record<string, { background: string; fontColor?: string; }> = {};
  if (!colorMap) {
    return out;
  }

  const registerTagKeys = (
    rawKey: string,
    tag: { background: string; fontColor?: string; },
    entryTagStyleAliases?: string[],
  ): void => {
    const registerOne = (k: string): void => {
      const trimmed = k.trim();
      if (!trimmed) {
        return;
      }
      out[trimmed] = tag;
      /** 小写键：name_en 任意大小写均可命中，减少 tagStyleAliases 中重复配置 */
      out[trimmed.toLowerCase()] = tag;
    };

    registerOne(rawKey);
    const fromChart = tagStyleAliases?.[rawKey.trim()];
    const merged = [
      ...(entryTagStyleAliases ?? []),
      ...(fromChart ?? []),
    ];
    if (merged.length) {
      merged.forEach(registerOne);
    }
  };

  const putTag = (
    key: string,
    background: string,
    fontColor: string | undefined,
    entryTagStyleAliases?: string[],
  ): void => {
    const tag: { background: string; fontColor?: string; } = {
      background,
    };
    if (typeof fontColor === 'string' && fontColor.trim() !== '') {
      tag.fontColor = fontColor.trim();
    }
    registerTagKeys(key, tag, entryTagStyleAliases);
  };

  if (isTopLevelKpiEventBarColorSplit(colorMap)) {
    const source = colorMap.eventTooltipsMap;
    if (!source) {
      return out;
    }
    Object.entries(source).forEach(([key, val]) => {
      if (key.startsWith('__')) {
        return;
      }
      const entryAliases =
        val &&
        typeof val === 'object' &&
        !Array.isArray(val) &&
        Array.isArray((val as TKpiEventTooltipColorEntry).tagStyleAliases)
          ? (val as TKpiEventTooltipColorEntry).tagStyleAliases
          : undefined;
      const { barBackground, barFontColor } = parseEventTypeBarColorEntry(val);
      if (!barBackground) {
        return;
      }
      putTag(key, barBackground, barFontColor, entryAliases);
    });
    return out;
  }

  if (isPerTypeKpiEventBarColorMap(colorMap)) {
    Object.entries(colorMap).forEach(([key, val]) => {
      if (key.startsWith('__')) {
        return;
      }
      if (
        typeof val === 'string' ||
        (val &&
          typeof val === 'object' &&
          !Array.isArray(val) &&
          !('eventTooltips' in val) &&
          !('xAxisBubble' in val))
      ) {
        const entryAliases =
          typeof val === 'object' &&
          val &&
          !Array.isArray(val) &&
          Array.isArray((val as { tagStyleAliases?: string[]; }).tagStyleAliases)
            ? (val as { tagStyleAliases?: string[]; }).tagStyleAliases
            : undefined;
        const { barBackground, barFontColor } = parseEventTypeBarColorEntry(
          val as TEventTypeBarColorValue,
        );
        if (!barBackground) {
          return;
        }
        putTag(key, barBackground, barFontColor, entryAliases);
        return;
      }
      if (
        !val ||
        typeof val !== 'object' ||
        Array.isArray(val) ||
        (!('eventTooltips' in val) && !('xAxisBubble' in val))
      ) {
        return;
      }
      const typed = val as TKpiEventTypeColorEntry;
      const { barBackground, barFontColor } = mergeKpiTypeTagColors(typed);
      if (!barBackground) {
        return;
      }
      putTag(key, barBackground, barFontColor, typed.tagStyleAliases);
    });
    return out;
  }

  const source = colorMap as Record<string, TEventTypeBarColorValue>;
  Object.entries(source).forEach(([key, val]) => {
    if (key.startsWith('__')) {
      return;
    }
    const entryAliases =
      typeof val === 'object' &&
      val &&
      !Array.isArray(val) &&
      Array.isArray((val as { tagStyleAliases?: string[]; }).tagStyleAliases)
        ? (val as { tagStyleAliases?: string[]; }).tagStyleAliases
        : undefined;
    const { barBackground, barFontColor } = parseEventTypeBarColorEntry(val);
    if (!barBackground) {
      return;
    }
    putTag(key, barBackground, barFontColor, entryAliases);
  });
  return out;
}

/**
 * KPI 底部 eventDetails 系列（X 轴旁「事件条数」数字气泡）样式。
 * 优先 `chartConfig.kpiXAxisEventCountBubble`；其次顶层拆分里的 `xAxisBubble`；保留键 `__kpiXAxisBubble__`。
 * 按类型分组的 `eventTypeBarColorMap` 内各类型 **xAxisBubble 不用于此处**（仅浮窗 Tag），避免轴气泡被类型色染色。
 */
export function extractKpiXAxisBubbleLabelColors(
  colorMap: TEventTypeBarColorMapConfig | undefined,
  explicitEventCountBubble?: TKpiXAxisBubbleLabelColors | null,
): TKpiXAxisBubbleLabelColors | undefined {
  if (explicitEventCountBubble && typeof explicitEventCountBubble === 'object') {
    return explicitEventCountBubble;
  }
  if (!colorMap) {
    return undefined;
  }
  if (isTopLevelKpiEventBarColorSplit(colorMap)) {
    const x = colorMap.xAxisBubble;
    return x && typeof x === 'object' ? x : undefined;
  }
  if (isPerTypeKpiEventBarColorMap(colorMap)) {
    return undefined;
  }
  const flat = colorMap as Record<string, TEventTypeBarColorValue>;
  const reserved = flat[EVENT_TYPE_BAR_COLOR_MAP_KEY_KPI_X_AXIS_BUBBLE];
  if (reserved && typeof reserved === 'object' && typeof reserved !== 'string') {
    const r = reserved as {
      xAxisBubble?: TKpiXAxisBubbleLabelColors;
    } & TKpiXAxisBubbleLabelColors;
    if (r.xAxisBubble && typeof r.xAxisBubble === 'object') {
      return r.xAxisBubble;
    }
    if (
      r.background !== undefined ||
      r.fontColor !== undefined ||
      r.borderColor !== undefined
    ) {
      return r as TKpiXAxisBubbleLabelColors;
    }
  }
  return undefined;
}

/**
 * 按 `event_type` 写入 `event_bar_color` / `event_bar_font_color`，供舆情 EventItem 与 KPI Tag 对齐。
 */
export const applyEventTypeBarColorMapToEventDataMap = (
  eventData: EventDataMap | undefined,
  colorMap: TEventTypeBarColorMapConfig | undefined,
): EventDataMap | undefined => {
  if (!eventData || !colorMap) {
    return eventData;
  }
  let source: Record<string, TEventTypeBarColorValue> | undefined;
  if (isTopLevelKpiEventBarColorSplit(colorMap)) {
    source = colorMap.eventTooltipsMap;
  } else if (isPerTypeKpiEventBarColorMap(colorMap)) {
    const merged: Record<string, TEventTypeBarColorValue> = {};
    Object.entries(colorMap).forEach(([key, val]) => {
      if (key.startsWith('__')) {
        return;
      }
      if (
        val &&
        typeof val === 'object' &&
        !Array.isArray(val) &&
        'eventTooltips' in val
      ) {
        const tip = (val as TKpiEventTypeColorEntry).eventTooltips;
        if (tip !== undefined) {
          merged[key] = tip;
        }
      }
    });
    source = merged;
  } else {
    source = colorMap as Record<string, TEventTypeBarColorValue>;
  }
  if (!source) {
    return eventData;
  }
  const typeKeys = Object.keys(source).filter(k => !k.startsWith('__'));
  if (typeKeys.length === 0) {
    return eventData;
  }

  const out: EventDataMap = {};
  Object.keys(eventData).forEach(dateKey => {
    const list = eventData[dateKey];
    if (!Array.isArray(list) || list.length === 0) {
      out[dateKey] = Array.isArray(list) ? [...list] : [];
      return;
    }
    out[dateKey] = list.map((item: EventDataItem) => {
      const raw = lookupEventTypeBarColorRaw(source, item?.event_type ?? item?.eventType);
      if (raw === undefined) {
        return item;
      }
      const { barBackground, barFontColor } = parseEventTypeBarColorEntry(raw);
      if (!barBackground && !barFontColor) {
        return item;
      }
      const next: EventDataItem = { ...item };
      if (barBackground) {
        next.event_bar_color = barBackground;
      }
      if (barFontColor) {
        next.event_bar_font_color = barFontColor;
      }
      return next;
    });
  });
  return out;
};

export type TEventAxisQueryDateType = 'daily' | 'weekly' | 'monthly' | 'hourly';

export interface IEventAxisValueToQueryDateOptions {
  /** 周粒度时取该周 ISO 周日；月粒度时取月末（见 YYYY-MM 分支） */
  dateType?: TEventAxisQueryDateType;
}

/** 周轴刻度（任意周内日期）→ 当周 ISO 周日 YYYY-MM-DD */
const eventAxisWeekValueToSundayQueryDate = (dayYmd: string): string => {
  const m = moment(dayYmd, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return dayYmd;
  }
  return m.endOf('isoWeek').format('YYYY-MM-DD');
};

/**
 * 事件层 x 轴日期 key（YYYYMMDD / YYYY-MM-DD / YYYY-MM 等）→ 详情页 query.date（YYYY-MM-DD）
 * - 月粒度 YYYY-MM → 当月最后一天
 * - 周粒度 YYYY-MM-DD → 当周 ISO 周日（与 weekFilter endOf('isoWeek') 一致）
 */
export const eventAxisValueToQueryDate = (
  axisValue: string,
  options?: IEventAxisValueToQueryDateOptions,
): string => {
  const s = String(axisValue ?? '').trim();
  if (!s) return s;
  const dateType = options?.dateType;
  // 1. 月轴：2024-03（仅 monthly）
  if (dateType === 'monthly' && s.length === 7 && s[4] === '-') {
    const m = moment(s, 'YYYY-MM', true);
    return m.isValid() ? m.endOf('month').format('YYYY-MM-DD') : s;
  }
  // 2. 日/周轴：2024-03-04（允许后面带时间，只取前 10 位）
  if (s.length >= 10 && s[4] === '-' && s[7] === '-') {
    const day = s.slice(0, 10);
    const m = moment(day, 'YYYY-MM-DD', true);
    if (m.isValid()) {
      if (dateType === 'weekly') {
        return m.endOf('isoWeek').format('YYYY-MM-DD');
      }
      return day; // daily 或未传 dateType 的 YYYY-MM-DD
    }
  }
  // 3. 兼容：20240304 / 其它可被 canonical 识别的字段（下载、KPI 等）
  const ymd = canonicalEventDateYyyymmdd(s);
  if (ymd) {
    const day = `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
    if (dateType === 'weekly') {
      return moment(day, 'YYYY-MM-DD', true).endOf('isoWeek').format('YYYY-MM-DD');
    }
    return day;
  }
  return s;
};

/**
 * 格式化事件数据（返回对象形式）
 * @description
 * 将后端返回的事件数据格式化为 { [date]: [records] } 的形式。
 * 每个事件仅在 start_time 对应的日期出现，重复事件（id 相同 或 (event_type + event_title_en) 相同）将被去重。
 * 
 * ⚙️ 功能特性：
 * - 去重：
 *   - id ≠ 0 → 按 id 去重；
 *   - id = 0 → 按 (event_type + event_title_en) 去重；
 * - 聚合：事件按 start_time 归类；
 * - 保留所有日期，即使当天没有事件；
 * - 🧩 兜底：若数据无效，返回空数组 []。
 *
 * @param {Array} data - 后端返回的事件列表数组
 * @param {string} data[].date - 当前日期 (YYYY-MM-DD)
 * @param {Object[]} [data[].event_list] - 当天的事件列表
 * @param {Object[]} [data[].event_list[].event_record] - 每个事件记录
 * @param {string|number} data[].event_list[].event_record[].id - 事件唯一ID
 * @param {string} data[].event_list[].event_record[].event_type - 事件类型
 * @param {string} data[].event_list[].event_record[].event_title_en - 英文标题
 * @param {string} data[].event_list[].event_record[].event_title_cn - 中文标题
 * @param {string} data[].event_list[].event_record[].start_time - 开始日期
 * @param {string} data[].event_list[].event_record[].end_time - 结束日期
 *
 * @returns {Record<string, Array<{
*   name: string,
*   name_cn: string,
*   start_time: string,
*   end_time: string
* }>> | []} 格式化后的事件数据：
* 
* {
*   "2025-10-06": [
*     {
*       name: "datalab事件",
*       name_cn: "datalab事件",
*       start_time: "2025-10-01",
*       end_time: "2025-10-08"
*     }
*   ],
*   "2025-10-07": []
* }
*
* 若 data 无效或无数据，则返回 []
*/
export const formatEventData = (
  data: IRawEventDayItem[] | undefined | null,
): Record<string, IFormattedEventRecord[]> | [] => {
  const list = data;

  // 🧩 兜底：没有有效数据则返回 []
  if (!Array.isArray(list) || list.length === 0) {
    return [];
  }

  // 去重集合（存储唯一 key）
  const seenKeys = new Set<string>();

  // start_time -> [record]
  const startTimeMap = new Map<string, IFormattedEventRecord[]>();

  list.forEach(item => {
    item.event_list?.forEach(eventItem => {
      eventItem.event_record?.forEach((record: TRawEventRecord) => {
        if (!record?.start_time) return;

        // 判断唯一 key
        const key =
         record.id && record.id !== 0
           ? `id_${record.id}`
           : `type_${record.event_type || ''}_title_${record.event_title_en || ''}`;

        // 去重逻辑
        if (seenKeys.has(key)) return;
        seenKeys.add(key);

        const start = record.start_time;
        if (!startTimeMap.has(start)) {
          startTimeMap.set(start, []);
        }

        startTimeMap.get(start)!.push({
          name: record.event_title_en,
          name_cn: record.event_title_cn,
          start_time: record.start_time,
          end_time: record.end_time,
        });
      });
    });
  });

  // 生成 { date: records[] } 对象
  const result: Record<string, IFormattedEventRecord[]> = {};

  list.forEach(item => {
    const { date } = item;
    result[date] = startTimeMap.get(date) || [];
  });

  return result;
};

/** 
 * 格式化数据源，针对【事件】下载进行处理
 * 主要用于下载 Excel 时，在数据表格中展示每个日期关联的事件信息。
 * 行日期与事件的匹配统一为 YYYY-MM-DD（支持 YYYYMMDD / ISO），避免 `new Date('20250401')` 无效导致整列为空。
 * 默认单元格展示事件名称（中/英文标题）；eventDisplay==='start_time' 时展示 dateKey 对应日期（一般为 start_time）。
 */
export const formatEventDataSource = (
  seriesData: { date: string; }[],
  eventData: unknown[] | Record<string, unknown>,
  downloadConfig: { columns: { dependKey: string; code: string; dateKey: string; }[]; },
  isEn: boolean,
) => {
  // 兜底：seriesData 无效则返回空数组
  if (!seriesData || !Array.isArray(seriesData)) {
    return [];
  }

  // 兜底：eventData 无效则直接返回原始数据（不进行事件匹配）
  if (!eventData || !Array.isArray(eventData)) {
    return seriesData;
  }

  /** 
   * 获取所有事件列配置
   * 只处理 type === 'event' 的列，其他列不进行事件匹配
   */
  const eventColumns = (downloadConfig?.columns as IDownloadColumnForEventMatch[] | undefined)?.filter(
    (column) => column?.type === 'event',
  ) || [];

  if (eventColumns.length === 0) {
    return seriesData;
  }

  /**
   * 遍历每个数据行，为其添加事件信息
   */
  // 行结构动态（由 seriesData 决定），额外写入事件列，使用 Record<string, unknown> 宽化保留原字段
  return seriesData.map((item: Record<string, unknown>) => {
    /** 
     * 动态构建事件数据映射
     * key: 事件列的 code（字段名）
     * value: 匹配的事件名称或开始时间（多个用逗号 + 空格分隔）
     */
    const eventDataMap: Record<string, string> = {};

    eventColumns.forEach((eventColumn: IDownloadColumnForEventMatch) => {
      const {
        dependKey,
        code,
        // 事件列里用于事件匹配的日期字段
        dateKey = 'date',
        /** 'start_time'：展示 dateKey 规范化日期；默认展示事件中英文标题 */
        eventDisplay,
      } = eventColumn;

      const codeKey = String(code ?? '');
      const rowYmd = eventAxisValueToQueryDate(
        String((dependKey ? item?.[dependKey] : '') ?? ''),
      );
      if (!rowYmd) {
        eventDataMap[codeKey] = '';
        return;
      }

      // eventData 为 unknown[] | Record<string, unknown>，此处进入分支前已过滤为数组
      const matchedEvents = (eventData as TRawEventRecord[]).filter((event) => {
        const evYmd = eventAxisValueToQueryDate(String(event?.[dateKey] ?? ''));
        return Boolean(evYmd && evYmd === rowYmd);
      });

      if (eventDisplay === 'start_time') {
        eventDataMap[codeKey] = matchedEvents
          .map((event) => {
            const raw = event?.[dateKey];
            const ymd = eventAxisValueToQueryDate(String(raw ?? ''));
            if (ymd) {
              return ymd;
            }
            return String(raw ?? '').trim();
          })
          .filter(Boolean)
          .join(', ') || '';
        return;
      }

      eventDataMap[codeKey] = matchedEvents
        .map((event) =>
          (isEn ? event?.event_title_en : event?.event_title_cn) || '',
        )
        .filter((title: string) => title)
        .join(', ') || '';
    });

    /**
     * 返回合并后的数据行
     * 保留原始数据的所有字段，并添加事件列
     */
    return {
      ...item,
      ...eventDataMap,
    };
  });
};

/** 归一为 YYYYMMDD，便于与 X 轴刻度、事件 start_time 比较 */
export const canonicalEventDateYyyymmdd = (
  input: string | undefined | null,
): string => {
  if (input === undefined || input === null) {
    return '';
  }
  const s = String(input).trim();
  if (!s) {
    return '';
  }
  if (/^\d{8}$/.test(s)) {
    return s;
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) {
    return `${m[1]}${m[2]}${m[3]}`;
  }
  const digits = s.replace(/\D/g, '');
  return digits.length >= 8 ? digits.slice(0, 8) : '';
};

type TXAxisTickYyyymmddBounds = {
  minYmd: string;
  maxYmd: string;
};

/** 从 xAxisData 解析所有可识别的刻度日，返回最早/最晚 YYYYMMDD（字符串序即时间序） */
const getXAxisTickYyyymmddBounds = (
  xAxisData: string[] | undefined,
): TXAxisTickYyyymmddBounds | undefined => {
  const axis = xAxisData || [];
  if (!Array.isArray(axis) || axis.length === 0) {
    return undefined;
  }
  const ymds = axis
    .map(v => canonicalEventDateYyyymmdd(v))
    .filter((s): s is string => Boolean(s));
  if (ymds.length === 0) {
    return undefined;
  }
  const sorted = [...ymds].sort();
  return {
    minYmd: sorted[0] as string,
    maxYmd: sorted[sorted.length - 1] as string,
  };
};

const isEventStartWithinClip = (
  stYmd: string,
  clip: TEventStartTimeAxisClipMode,
  minYmd: string,
  maxYmd: string,
): boolean => {
  if (clip === 'before_min') {
    return stYmd >= minYmd;
  }
  return stYmd >= minYmd && stYmd <= maxYmd;
};

const shouldDropDayBucket = (
  dayYmd: string,
  clip: TEventStartTimeAxisClipMode,
  minYmd: string,
  maxYmd: string,
): boolean => {
  if (!dayYmd) {
    return false;
  }
  if (clip === 'before_min') {
    return dayYmd < minYmd;
  }
  return dayYmd < minYmd || dayYmd > maxYmd;
};

/**
 * 与趋势图 X 轴刻度对齐，按 `clip` 裁剪事件：
 * - `before_min`：去掉早于最早刻度日的事件，不裁晚于最晚刻度的。
 * - `within_range`：仅保留 [最早刻度, 最晚刻度] 内。
 * - `false`：不裁剪。
 */
export const filterEventDataMapDropStartBeforeFirstAxisTick = (
  map: EventDataMap | undefined,
  xAxisData: string[] | undefined,
  clip: TEventStartTimeAxisClipMode | false = 'before_min',
): EventDataMap | undefined => {
  if (clip === false || !map || typeof map !== 'object') {
    return map;
  }
  const bounds = getXAxisTickYyyymmddBounds(xAxisData);
  if (!bounds) {
    return map;
  }
  const { minYmd, maxYmd } = bounds;
  const out: EventDataMap = {};
  Object.keys(map).forEach(dayKey => {
    const dayYmd = canonicalEventDateYyyymmdd(dayKey);
    if (shouldDropDayBucket(dayYmd, clip, minYmd, maxYmd)) {
      return;
    }
    const items = map[dayKey];
    if (!Array.isArray(items) || items.length === 0) {
      return;
    }
    const kept = items.filter((it: EventDataItem) => {
      const rec = it as Record<string, unknown>;
      const st = rec?.start_time ?? rec?.startTime;
      const stYmd = canonicalEventDateYyyymmdd(
        typeof st === 'string' ? st : String(st ?? ''),
      );
      if (!stYmd) {
        return true;
      }
      return isEventStartWithinClip(stYmd, clip, minYmd, maxYmd);
    });
    if (kept.length > 0) {
      out[dayKey] = kept;
    }
  });
  return out;
};

/** 与 filterEventDataMapDropStartBeforeFirstAxisTick 同源，供下载/表格与图一致 */
export const filterRawEventListDropStartBeforeFirstAxisTick = (
  list: RawEventItem[] | undefined,
  xAxisData: string[] | undefined,
  clip: TEventStartTimeAxisClipMode | false = 'before_min',
): RawEventItem[] | undefined => {
  if (clip === false || !Array.isArray(list) || list.length === 0) {
    return list;
  }
  const bounds = getXAxisTickYyyymmddBounds(xAxisData);
  if (!bounds) {
    return list;
  }
  const { minYmd, maxYmd } = bounds;
  return list.filter(r => {
    const st = (r as Record<string, unknown>).start_time;
    const stYmd = canonicalEventDateYyyymmdd(
      typeof st === 'string' ? st : String(st ?? ''),
    );
    if (!stYmd) {
      return true;
    }
    return isEventStartWithinClip(stYmd, clip, minYmd, maxYmd);
  });
};
